import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

const API_BASE_URL = process.env.NEXT_PUBLIC_API_BASE_URL;

export interface CustomPortrait {
  _id: string;
  userId: string;
  portraitImages: string[];
  // Legacy fields for backward compatibility
  porttraitImage?: string[];
  fullName: string;
  email: string;
  phoneNumber: string;
  deliveryAddress: string;
  isAgreed: boolean;
  // Legacy fields for backward compatibility
  isAggreed?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CustomPortraitResponse {
  success: boolean;
  // statusCode is not in the response body
  message: string;
  data: CustomPortrait[];
  meta: {
    page: number;
    limit: number;
    total: number;
    totalPage: number;
  };
}

export const customPortraitApi = createApi({
  reducerPath: "customPortraitApi",
  baseQuery: fetchBaseQuery({
    baseUrl: API_BASE_URL,
    prepareHeaders: (headers) => {
      const token = localStorage.getItem("accessToken");
      if (token) {
        headers.set("Authorization", `Bearer ${token}`);
      }
      return headers;
    },
    credentials: "include",
  }),
  tagTypes: ["CustomPortrait"],
  endpoints: (builder) => ({
    getCustomPortraits: builder.query<
      CustomPortraitResponse,
      { page?: number; limit?: number; searchTerm?: string }
    >({
      query: (params) => {
        const queryParams = new URLSearchParams();
        if (params.page) queryParams.append("page", params.page.toString());
        if (params.limit) queryParams.append("limit", params.limit.toString());
        if (params.searchTerm)
          queryParams.append("searchTerm", params.searchTerm);

        return {
          url: `/custom-portrait?${queryParams.toString()}`,
          method: "GET",
        };
      },
      providesTags: ["CustomPortrait"],
      transformResponse: (response: CustomPortraitResponse) => {
        return {
          ...response,
          data: response.data.map((item) => ({
            ...item,
            portraitImages:
              item.portraitImages && item.portraitImages.length > 0
                ? item.portraitImages
                : item.porttraitImage || [],
            isAgreed: item.isAgreed || item.isAggreed || false,
          })),
        };
      },
    }),
    getCustomPortraitById: builder.query<
      { success: boolean; data: CustomPortrait },
      string
    >({
      query: (id) => `/custom-portrait/${id}`,
      providesTags: (result, error, id) => [{ type: "CustomPortrait", id }],
      transformResponse: (response: {
        success: boolean;
        data: CustomPortrait & {
          porttraitImage?: string[];
          isAggreed?: boolean;
        };
      }) => {
        return {
          ...response,
          data: {
            ...response.data,
            portraitImages:
              response.data.portraitImages &&
              response.data.portraitImages.length > 0
                ? response.data.portraitImages
                : response.data.porttraitImage || [],
            isAgreed:
              response.data.isAgreed || response.data.isAggreed || false,
          },
        };
      },
    }),
    updateCustomPortrait: builder.mutation<
      { success: boolean; data: CustomPortrait },
      { id: string; data: Partial<CustomPortrait>; images?: File[] }
    >({
      query: ({ id, data, images }) => {
        const formData = new FormData();

        // Append text fields
        Object.entries(data).forEach(([key, value]) => {
          if (value !== undefined) {
            formData.append(key, value.toString());
          }
        });

        // Append images if present
        if (images && images.length > 0) {
          images.forEach((image) => {
            formData.append("portraitImages", image);
          });
        }

        return {
          url: `/custom-portrait/update-custom-portrait/${id}`,
          method: "PATCH",
          body: formData,
        };
      },
      invalidatesTags: ["CustomPortrait"],
    }),
    deleteCustomPortrait: builder.mutation<{ success: boolean }, string>({
      query: (id) => ({
        url: `/custom-portrait/delete-custom-portrait/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["CustomPortrait"],
    }),
  }),
});

export const {
  useGetCustomPortraitsQuery,
  useGetCustomPortraitByIdQuery,
  useUpdateCustomPortraitMutation,
  useDeleteCustomPortraitMutation,
} = customPortraitApi;
